import type {ApolloError} from "@apollo/client";
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Edge,
  type EdgeAddChange,
  type EdgeChange,
  type Node,
  type NodeAddChange,
  type NodeChange,
} from "@xyflow/react";
import PubSub from "pubsub-js";
import {is, map} from "ramda";
import {ZodError} from "zod";
import type {StateCreator} from "zustand";

import type {WorkflowEvent} from "@/graphql.ts";
import {PubSubActions} from "@/global/pubsub-actions.ts";
import {intl} from "@/messages/message.ts";
import {notify} from "@/modules/notifications/notify";
import {
  getWorkflowEventDetails,
  importTemplateToWorkflowEventMutation,
  loadWorkflowEventQuery,
  loadWorkflowQuery,
} from "@/modules/workflow/graphql/queries.ts";
import type {WorkflowConfigActivityFragment} from "@/modules/workflow/graphql/workflow.gql.ts";
import {workflowIntl} from "@/modules/workflow/i18n/intl.ts";
import {initialErrorsSliceState} from "@/modules/workflow/stores/errors/workflow-editor-store-errors-slice";
import {initialExecutionSliceState} from "@/modules/workflow/stores/execution/workflow-editor-store-execution-slice";
import {initialReactFlowSliceState} from "@/modules/workflow/stores/reactflow/workflow-editor-store-reactflow-slice";
import type {
  WorkflowEditorStore,
  WorkflowLoadSaveSlice,
  WorkflowLoadSaveSliceState,
} from "@/modules/workflow/stores/workflow-editor-store-types";
import {
  activityToEditorNode,
  connectorToEdge,
  getWorkflowEventInputForSaving,
} from "@/modules/workflow/utils/generateWorkflow.ts";
import {
  type EdgeData,
  type EditorNodeData,
  ExtendedJobStatusEnum,
  type WorkflowLoadData,
} from "@/modules/workflow/utils/types.ts";

export const initialLoadSaveSliceState: WorkflowLoadSaveSliceState = {
  isLoadingWorkflow: false,
  isSavingWorkflow: false,
  showPublishTemplateDialog: false,
  workflowEventDetails: null,
  autoSaveErrorMessage: null,
};

// ! We auto save the workflow in WorkflowEditorStoreProvider

export const createLoadSaveSlice: StateCreator<WorkflowEditorStore, [], [], WorkflowLoadSaveSlice> = (set, get) => {
  PubSub.subscribe(PubSubActions.LOGOUT, () => {
    set(initialLoadSaveSliceState);
  });

  return {
    ...initialLoadSaveSliceState,
    loadWorkflowEvent: async (workflowEventId) => {
      if (is(Number, workflowEventId) && !!get().instance) {
        // Skip if already loading or if the workflow event ID is the same as the currently loaded one
        const isCurrentlyLoading = get().isLoadingWorkflow;
        const currentWorkflowEventId = get().workflowEvent.id;

        // If we're already loading this workflow event or it's already loaded and initialized, skip
        if (isCurrentlyLoading || currentWorkflowEventId === workflowEventId) {
          return;
        }

        set({ isLoadingWorkflow: true, copyPasteSessionData: null, autoSaveErrorMessage: null });

        // We update only when we failed to fetch before as parsing the activity.json is slow.
        // If activities have changed the page must be reload to get the latest changes
        if (get().activities.length === 0) {
          await get().loadActivities();
        }

        const { data, error } = await loadWorkflowEventQuery({ workflowEventId });
        const workflowEvent: WorkflowEvent | undefined | null = data?.workflow.findWorkflowEventById as WorkflowEvent;

        if (error || !workflowEvent) {
          throw new Error(
            (error as ApolloError | undefined)?.message ??
              workflowIntl.formatMessage({ id: workflowEvent ? "an error occurred" : "no workflow" }),
          );
        }

        if (workflowEvent) {
          // Resurrect nodes
          const nodes = map(activityToEditorNode, workflowEvent.config.activities as WorkflowConfigActivityFragment[]);
          const addNodeChanges: NodeChange[] = map((item) => ({ type: "add", item }) satisfies NodeAddChange, nodes);

          // Resurrect edges
          const edges = map((connector) => addEdge(connectorToEdge(connector), [])[0], workflowEvent.config.connectors);
          const addEdgeChanges: EdgeChange[] = map((item) => ({ type: "add", item }) satisfies EdgeAddChange, edges);

          set({
            errors: initialErrorsSliceState.errors,
            parametersRecord: {},
            selectedActivity: initialReactFlowSliceState.selectedActivity,
            currentConnection: initialReactFlowSliceState.currentConnection,
            workflowEvent: {
              ...initialExecutionSliceState.workflowEvent,
              workflowId: workflowEvent.workflow?.id,
              retentionRule: workflowEvent.retentionRule,
              folderId: workflowEvent.config.outputFolder?.id,
              id: workflowEvent.workflowEventId,
              name: workflowEvent.alias,
              description: workflowEvent.note,
              projectId: workflowEvent.project?.id,
              extendedExecutionStatus:
                (workflowEvent.status as ExtendedJobStatusEnum | null) ?? ExtendedJobStatusEnum.Ready,
            },
            sampleTypeId: workflowEvent.config.sampleType?.id,
            nodes: applyNodeChanges(addNodeChanges, []) as Node<EditorNodeData>[],
            edges: applyEdgeChanges(addEdgeChanges, []) as Edge<EdgeData>[],
            timeline: [
              {
                nodes: nodes,
                edges: applyEdgeChanges(addEdgeChanges, []) as Edge<EdgeData>[],
                parameters: [],
              },
            ],
          });

          // next tick
          setTimeout(() => {
            const instance = get().instance;
            instance?.fitView({ maxZoom: 1 });
          }, 500);

          // get the full set of metadata of an event. Previously we fetched only very basic information to keep
          // the loading time reasonable. No need to synchronize this, it can update in the background
          void get().refreshWorkflowEventDetails();

          // resurrect parameters
          for (const activity of workflowEvent.config.activities) {
            await get().registerActivityParameters(activity.identifier, activity.parameters);
          }
          get().checkAllParameters();
        }

        // Modifying nodes and edges makes it dirty, just reset it because it's freshly loaded.
        set({ isLoadingWorkflow: false });
      }
    },
    async importWorkflowTemplate(workflowId: number) {
      if (is(Number, workflowId) && !!get().instance) {
        set({ isLoadingWorkflow: true, autoSaveErrorMessage: null });

        if (get().activities.length === 0) {
          try {
            await get().loadActivities();
          } catch {
            set({ isLoadingWorkflow: false });
            return;
          }
        }

        try {
          const { data, error } = await loadWorkflowQuery({ workflowId });

          const workflow: WorkflowLoadData | undefined | null = data?.workflow.findWorkflowById;
          if (error || !workflow) {
            set({ isLoadingWorkflow: false });
            notify.fromErrors({
              title: "Loading Workflow Error",
              errors: [
                new Error(
                  (error as ApolloError | undefined)?.message ??
                    workflowIntl.formatMessage({ id: workflow ? "an error occurred" : "no workflow" }),
                ),
              ],
            });
          } else if (workflow) {
            // Resurrect nodes
            const nodes = workflow.config.activities.map((activity) => activityToEditorNode(activity));
            const addNodeChanges: NodeChange[] = map((item) => ({ type: "add", item }) satisfies NodeAddChange, nodes);

            // Resurrect edges
            const edges = map((connector) => addEdge(connectorToEdge(connector), [])[0], workflow.config.connectors);
            const addEdgeChanges: EdgeChange[] = map((item) => ({ type: "add", item }) satisfies EdgeAddChange, edges);

            const extendedExecutionStatus = ExtendedJobStatusEnum.Ready;

            set({
              nodes: applyNodeChanges(addNodeChanges, []) as Node<EditorNodeData>[],
              edges: applyEdgeChanges(addEdgeChanges, []) as Edge<EdgeData>[],
              sampleTypeId: workflow.config.sampleType?.id,
              activityErrors: [],
              errors: [],
            });

            // next tick
            setTimeout(() => {
              const instance = get().instance;
              instance?.fitView({ maxZoom: 1 });
            }, 500);

            void get().refreshWorkflowEventDetails();

            // resurrect parameters
            for (const activity of workflow.config.activities) {
              // Will also set remaining mandatory parameters with `Nothing` and not set non-existent parameters
              await get().registerActivityParameters(activity.identifier, activity.parameters);
            }

            get().checkAllParameters();

            // Save event with imported workflow
            set({ isSavingWorkflow: true });
            const { data: saveData, error: saveError } = await importTemplateToWorkflowEventMutation({
              workflow: {
                ...getWorkflowEventInputForSaving([
                  get().workflowEvent,
                  nodes,
                  edges as Edge<EdgeData>[],
                  get().parametersRecord,
                  get().sampleTypeId,
                ]),
                workflowId: workflowId,
              },
            });
            set({ isSavingWorkflow: false });

            if (saveError) {
              set({ workflowEvent: { ...get().workflowEvent, extendedExecutionStatus } });
              throw saveError;
            }

            const id = saveData?.workflow.importTemplateToWorkflowEvent.workflowEventId;

            if (id == null || id !== get().workflowEvent.id) {
              set({ workflowEvent: { ...get().workflowEvent, extendedExecutionStatus } });
              throw new Error("Saving error");
            }
            set({
              isLoadingWorkflow: false,
              workflowEvent: { ...get().workflowEvent, workflowId },
            });
          }
        } catch (error) {
          set({ isSavingWorkflow: false, isLoadingWorkflow: false });
          if (error instanceof ZodError) {
            set({ autoSaveErrorMessage: [...new Set(error.issues.map((i) => i.message))].join(", ") });
          } else if (error instanceof Error) {
            set({ autoSaveErrorMessage: error.message });
          } else {
            set({ autoSaveErrorMessage: intl.formatMessage({ id: "an error occurred" }) });
          }
          throw error;
        }
      }
    },
    async refreshWorkflowEventDetails() {
      const workflowEventId = get().workflowEvent.id;

      if (workflowEventId != undefined) {
        const { data } = await getWorkflowEventDetails({ workflowEventId });
        const details = data?.workflow.findWorkflowEventById;

        if (details != undefined) {
          set({ workflowEventDetails: details });
        }
      }
    },
  };
};
